import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './EmployeeManagement.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Icons } from '../components/Icons';
import { FaInbox, FaSearch } from 'react-icons/fa';
import { apiFetch } from '../utils/api';

const INITIAL_FORM_STATE = {
  first_name: '', middle_name: '', last_name: '', email: '', phone: '',
  sin: '', date_of_birth: '', address: '', city: '', province: 'ON', postal_code: '',
  employment_start_date: '', employment_end_date: '', employment_status: 'Active', job_title: '', ei_exempt: 'No', cpp_exempt: 'No',
  pay_group: 'Hourly', hourly_rate: '', salary: '', pay_frequency: 'biweekly',
  vacation_pay_additional: 'No', tax_claim_code: 'TD1', is_t4a: 'No',
  exclude_from_t4: 'No', email_slip: 'No', direct_deposit_active: 'No',
  bank_no: '', transit_no: '', account_no: '',
};

const FORM_SECTIONS = [
  { number: 1, title: 'Personal Information' },
  { number: 2, title: 'Contact & Location' },
  { number: 3, title: 'Employment & Compensation' },
  { number: 4, title: 'Tax & Settings' },
];

const STATUS_STYLES = {
  'Active': { bg: 'rgba(28,138,92,0.12)', color: '#1C8A5C', border: 'rgba(28,138,92,0.28)' },
  'On Leave': { bg: 'rgba(200,155,60,0.14)', color: '#8A6A24', border: 'rgba(200,155,60,0.32)' },
  'Inactive': { bg: 'rgba(107,114,128,0.12)', color: '#6B7280', border: 'rgba(107,114,128,0.28)' },
};

function EmployeeManagement({ org, instance, userRole }) {
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [notification, setNotification] = useState(null);
  const [activeSection, setActiveSection] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const sectionRefs = useRef({});
  const drawerBodyRef = useRef(null);

  // Load display + data typefaces once
  useEffect(() => {
    if (document.getElementById('ledger-font-link')) return;
    const link = document.createElement('link');
    link.id = 'ledger-font-link';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  const triggerNotification = (message, type = 'success') => {
    const options = { position: "top-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true };
    switch (type) {
      case 'success': toast.success(message, options); break;
      case 'danger': toast.error(message, options); break;
      case 'warning': toast.warn(message, options); break;
      case 'info': toast.info(message, options); break;
      default: toast(message, options); break;
    }
  };

  const fetchEmployees = useCallback(async (showLoading = true) => {
    if (!org?.id) return;
    setLoading(true);
    try {
      // App.js passes tenantId to components that need it.
      // We need to get it from there. For now, let's assume it's part of the `org` object or passed separately.
      // Let's assume `tenantId` is available in this component's scope.
      const data = await apiFetch(`/organizations/${org.id}/employees`, org.tenantId);
      setEmployees(data);
    } catch (error) {
      triggerNotification('Error fetching employee directory.', 'danger');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [org?.id]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Lock background scrolling when drawer is open
  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showForm]);

  // Scroll-spy for the ledger tab index — highlights the section currently in view
  useEffect(() => {
    if (!showForm) return;
    const root = drawerBodyRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(Number(entry.target.dataset.section));
          }
        });
      },
      { root, threshold: 0, rootMargin: '0px 0px -70% 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [showForm]);

  const scrollToSection = (number) => {
    sectionRefs.current[number]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'Yes' : 'No') : value
    }));
  };

  const handlePlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      address: placeData.address || prev.address,
      city: placeData.city || prev.city,
      province: placeData.province || prev.province,
      postal_code: placeData.postal_code || prev.postal_code
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.sin || !formData.address) {
      triggerNotification('First name, last name, SIN, and address are required.', 'danger');
      return;
    }

    const roleLimits = { 'l1': 1, 'l2': 10 };
    if (userRole && roleLimits[userRole] && !editingId) {
      if (employees.length >= roleLimits[userRole]) {
        triggerNotification(`Your role (${userRole.toUpperCase()}) is limited to ${roleLimits[userRole]} employee(s).`, 'danger');
        return;
      }
    }

    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) {
      triggerNotification('Could not identify user. Please sign in again.', 'danger');
      return;
    }

    const submitData = {
      ...formData,
      is_t4a: formData.is_t4a === 'Yes' ? 1 : 0,
      cpp_exempt: formData.cpp_exempt === 'Yes' ? 1 : 0,
      ei_exempt: formData.ei_exempt === 'Yes' ? 1 : 0,
      vacation_pay_additional: formData.vacation_pay_additional === 'Yes' ? 1 : 0,
      requesting_user_email: activeAccount.username,
    };

    const endpoint = editingId ? `/employees/${editingId}` : `/organizations/${org.id}/employees`;
    const method = editingId ? 'PUT' : 'POST';

    try {
      await apiFetch(endpoint, org.tenantId, { method, body: submitData });
        triggerNotification(`Employee successfully ${editingId ? 'updated' : 'added'}.`);
        resetForm();
        fetchEmployees();
    } catch (error) {
      triggerNotification('Network error saving employee.', 'danger');
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      ...employee,
      is_t4a: employee.is_t4a === 1 ? 'Yes' : 'No',
      cpp_exempt: employee.cpp_exempt === 1 ? 'Yes' : 'No',
      ei_exempt: employee.ei_exempt === 1 ? 'Yes' : 'No',
      vacation_pay_additional: employee.vacation_pay_additional === 1 ? 'Yes' : 'No',
    });
    setEditingId(employee.id);
    setActiveSection(1);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently remove this employee?')) return;
    try {
      await apiFetch(`/employees/${id}`, org.tenantId, { method: 'DELETE' });
      triggerNotification('Employee successfully removed.');
      fetchEmployees();
    } catch (error) {
      triggerNotification('Network error deleting employee.', 'danger');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setEditingId(null);
    setActiveSection(1);
    setShowForm(false);
  };

  const getInitials = (first, last) => `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`.toUpperCase();

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(emp =>
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q) ||
      (emp.email || '').toLowerCase().includes(q) ||
      (emp.job_title || '').toLowerCase().includes(q)
    );
  }, [employees, searchQuery]);

  if (!org) return null; // Wait for org context

  return (
    <div className="ledger-app container-fluid py-4 px-3 px-md-4" style={{ backgroundColor: 'var(--ledger-canvas)', minHeight: '100vh' }}>
      {/* Add ToastContainer here. Ideally, this should be in your root App.js */}
      <ToastContainer />

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center mb-1 gap-2">
            <Icons.Users />
            <h1 className="h4 ledger-page-title mb-0">Employee Directory</h1>
          </div>
          <p className="ledger-subtitle small mb-0 mt-1">Manage personnel records, compensation, and tax settings for <strong>{org.name}</strong>.</p>
        </div>
        {userRole === 'l1' && employees.length >= 1 ? (
          <button className="btn btn-secondary d-flex align-items-center justify-content-center fw-semibold px-4 shadow-sm" disabled title="Employee limit for your role has been reached.">
            <Icons.Plus /> Employee Limit Reached
          </button>
        ) : userRole === 'l2' && employees.length >= 10 ? (
          <button className="btn btn-secondary d-flex align-items-center justify-content-center fw-semibold px-4 shadow-sm" disabled title="Employee limit for your role has been reached.">
            <Icons.Plus /> Employee Limit Reached
          </button>
        ) : (
          <button className="btn btn-primary d-flex align-items-center justify-content-center fw-semibold px-4 shadow-sm" onClick={() => { resetForm(); setShowForm(true); }}>
            <Icons.Plus /> Add New Employee
          </button>
        )}
      </div>

      <div className="row g-4">

        {/* Main Directory Table */}
        <div className="col-12">
          <div className="ledger-card shadow-sm h-100">
            <div className="card-body p-0">

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 p-md-4 ledger-card-header rounded-top" style={{ borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
                <h2 className="h6 fw-bold mb-0" style={{ color: 'var(--ledger-ink)' }}>Active Personnel</h2>
                <div className="d-flex align-items-center gap-3 flex-wrap ledger-header-actions">
                  <div className="ledger-search-wrap">
                    <FaSearch />
                    <input
                      type="text"
                      className="form-control ledger-search-input py-2"
                      placeholder="Search Employee"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <span className="badge badge-count px-3 py-2 rounded-pill shadow-sm">
                    {employees.length} Employee{employees.length !== 1 && 's'}
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="p-5 text-center ledger-subtitle d-flex flex-column align-items-center">
                  <div className="spinner-border mb-3" role="status" style={{ color: 'var(--ledger-primary)' }}></div>
                  <span>Loading directory data...</span>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="p-5 text-center ledger-subtitle">
                  <div className="emp-empty-icon">
                    <FaInbox />
                  </div>
                  <h5 className="fw-bold" style={{ color: 'var(--ledger-ink)' }}>
                    {employees.length === 0 ? 'No employees found' : 'No matches for your search'}
                  </h5>
                  <p className="mb-0">
                    {employees.length === 0 ? 'Add your first employee to start managing payroll.' : 'Try a different name, email, or job title.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Phone layout: stacked cards. A table can only shrink so far before
                      real content (names, two action buttons) forces a scrollbar, so
                      below `sm` we drop the table entirely instead of fighting it. */}
                  <div className="d-sm-none emp-card-list">
                    {filteredEmployees.map(emp => {
                      const statusStyle = STATUS_STYLES[emp.employment_status] || STATUS_STYLES['Inactive'];
                      return (
                        <div key={emp.id} className="emp-card border-bottom p-3">
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-3 min-w-0">
                              <div className="emp-avatar rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '0.85rem' }}>
                                {getInitials(emp.first_name, emp.last_name)}
                              </div>
                              <div className="min-w-0">
                                <div className="fw-bold text-truncate" style={{ color: 'var(--ledger-ink)' }}>
                                  {emp.first_name} {emp.last_name}
                                </div>
                                <div className="small ledger-subtitle text-capitalize text-truncate">
                                  {emp.job_title || emp.pay_group}
                                </div>
                              </div>
                            </div>
                            <span
                              className="badge status-pill rounded-pill px-2 py-1 flex-shrink-0"
                              style={{ background: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border, fontSize: '0.65rem' }}
                            >
                              {emp.employment_status}
                            </span>
                          </div>

                          <div className="row g-2 mt-2">
                            <div className="col-6">
                              <div className="small ledger-subtitle" style={{ fontSize: '0.68rem' }}>PAY</div>
                              <div className="ledger-mono fw-semibold small">
                                {emp.hourly_rate ? `$${parseFloat(emp.hourly_rate).toFixed(2)}/hr` : ''}
                                {emp.salary ? `$${parseFloat(emp.salary).toLocaleString('en-US', { minimumFractionDigits: 2 })}/yr` : ''}
                                {!emp.hourly_rate && !emp.salary && '--'}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="small ledger-subtitle" style={{ fontSize: '0.68rem' }}>EMAIL</div>
                              <div className="small fw-medium text-truncate">{emp.email || '--'}</div>
                            </div>
                            {emp.is_t4a === 1 && (
                              <div className="col-12">
                                <span className="badge py-1 px-2" style={{ fontSize: '0.65rem', background: 'rgba(47,111,237,0.1)', color: 'var(--ledger-primary)', border: '1px solid rgba(47,111,237,0.25)' }}>T4A Contractor</span>
                              </div>
                            )}
                          </div>

                          <div className="d-flex gap-2 mt-3">
                            <button type="button" className="btn btn-sm emp-action-btn shadow-sm flex-grow-1" onClick={() => handleEdit(emp)}>
                              <Icons.Edit /> <span className="ms-1">Edit</span>
                            </button>
                            <button type="button" className="btn btn-sm emp-action-btn danger shadow-sm flex-grow-1" onClick={() => handleDelete(emp.id)}>
                              <Icons.Trash /> <span className="ms-1">Delete</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Tablet/desktop layout: real table, sm breakpoint and up */}
                  <div className="table-responsive d-none d-sm-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="ledger-thead text-uppercase border-bottom" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                        <tr>
                          <th className="px-4 py-3 border-0">Employee</th>
                          <th className="py-3 border-0 d-none d-lg-table-cell">Contact</th>
                          <th className="py-3 border-0">Compensation</th>
                          <th className="py-3 border-0">Status</th>
                          <th className="text-end px-4 py-3 border-0">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.map(emp => {
                          const statusStyle = STATUS_STYLES[emp.employment_status] || STATUS_STYLES['Inactive'];
                          return (
                          <tr key={emp.id} className="border-bottom emp-row">
                            <td className="px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="emp-avatar rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '0.85rem' }}>
                                  {getInitials(emp.first_name, emp.last_name)}
                                </div>
                                <div className="min-w-0">
                                  <div className="fw-bold d-flex align-items-center flex-wrap gap-2" style={{ color: 'var(--ledger-ink)' }}>
                                    {emp.first_name} {emp.last_name}
                                    {emp.is_t4a === 1 && <span className="badge py-1 px-2" style={{ fontSize: '0.65rem', background: 'rgba(47,111,237,0.1)', color: 'var(--ledger-primary)', border: '1px solid rgba(47,111,237,0.25)' }}>T4A</span>}
                                  </div>
                                  <div className="small ledger-subtitle text-capitalize">
                                    {emp.job_title || emp.pay_group} • {emp.pay_frequency}
                                  </div>
                                  <div className="small ledger-subtitle d-lg-none mt-1">
                                    {emp.email || '--'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 d-none d-lg-table-cell">
                              <div className="small fw-medium" style={{ color: 'var(--ledger-text)' }}>{emp.email || '--'}</div>
                              <div className="small ledger-subtitle">{emp.phone || '--'}</div>
                            </td>
                            <td className="py-3">
                              <div className="ledger-mono fw-semibold small text-nowrap" style={{ color: 'var(--ledger-ink)' }}>
                                {emp.hourly_rate ? `$${parseFloat(emp.hourly_rate).toFixed(2)}/hr` : ''}
                                {emp.salary ? `$${parseFloat(emp.salary).toLocaleString('en-US', { minimumFractionDigits: 2 })}/yr` : ''}
                              </div>
                            </td>
                            <td className="py-3">
                              <span
                                className="badge status-pill rounded-pill px-3 py-1"
                                style={{ background: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}
                              >
                                {emp.employment_status}
                              </span>
                            </td>
                            <td className="text-end px-4 py-3 text-nowrap">
                              <button type="button" className="btn btn-sm emp-action-btn me-2 shadow-sm" onClick={() => handleEdit(emp)} title="Edit Profile">
                                <Icons.Edit /> <span className="d-none d-md-inline ms-1">Edit</span>
                              </button>
                              <button type="button" className="btn btn-sm emp-action-btn danger shadow-sm" onClick={() => handleDelete(emp.id)} title="Delete Profile">
                                <Icons.Trash />
                              </button>
                            </td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Side Drawer Overlay for Employee Form */}
      {showForm && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 drawer-backdrop"
            style={{ zIndex: 1040 }}
            onClick={resetForm}
          ></div>

          {/* Sliding Panel */}
          <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-flex flex-column"
            style={{
              width: '100%',
              maxWidth: '720px',
              zIndex: 1050,
              animation: 'slideInRight 0.3s ease-out forwards'
            }}
          >
            {/* Drawer Header */}
            <div className="ledger-drawer-header p-3 p-md-4 d-flex justify-content-between align-items-center flex-shrink-0">
              <h3 className="h5 fw-bold mb-0">{editingId ? 'Edit Employee Profile' : 'Register New Employee'}</h3>
              <button type="button" className="btn-close shadow-none" onClick={resetForm} aria-label="Close"></button>
            </div>

            {/* Mobile ledger tab index — horizontal scrollable pills, md breakpoint and below */}
            <div className="ledger-tab-bar-mobile">
              {FORM_SECTIONS.map(s => (
                <button
                  key={s.number}
                  type="button"
                  className={`ledger-tab-pill ${activeSection === s.number ? 'active' : ''}`}
                  onClick={() => scrollToSection(s.number)}
                >
                  <span>{s.number}</span>
                  <span>{s.title}</span>
                </button>
              ))}
            </div>

            <div className="d-flex flex-grow-1 overflow-hidden">
              {/* Ledger tab index — signature navigation element (desktop/tablet) */}
              <div className="ledger-stepper d-none d-md-flex flex-column">
                {FORM_SECTIONS.map(s => (
                  <button
                    key={s.number}
                    type="button"
                    className={`ledger-tab ${activeSection === s.number ? 'active' : ''}`}
                    onClick={() => scrollToSection(s.number)}
                  >
                    <span className="ledger-tab-number">{s.number}</span>
                    <span className="ledger-tab-title">{s.title}</span>
                  </button>
                ))}
              </div>

              {/* Drawer Body (Scrollable) */}
              <div ref={drawerBodyRef} className="flex-grow-1 overflow-auto p-3 p-md-4 bg-white">
                <form id="employee-form" onSubmit={handleSubmit}>

                  {/* 1. Personal Info */}
                  <h4
                    ref={el => sectionRefs.current[1] = el}
                    data-section={1}
                    className="form-section-title h6 mb-3 fw-bold border-bottom pb-2"
                    style={{ fontSize: '0.85rem' }}
                  >
                    <span className="section-number">{FORM_SECTIONS[0].number}</span>
                    <span className="text-uppercase">{FORM_SECTIONS[0].title}</span>
                  </h4>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">First Name <span style={{ color: 'var(--ledger-seal)' }}>*</span></label>
                      <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Last Name <span style={{ color: 'var(--ledger-seal)' }}>*</span></label>
                      <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Middle Name</label>
                      <input type="text" className="form-control" name="middle_name" value={formData.middle_name} onChange={handleInputChange} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Date of Birth</label>
                      <input type="date" className="form-control text-muted" name="date_of_birth" value={formData.date_of_birth} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Social Insurance Number (SIN) <span style={{ color: 'var(--ledger-seal)' }}>*</span></label>
                      <input type="text" className="form-control ledger-mono" name="sin" value={formData.sin} onChange={handleInputChange} placeholder="XXX-XXX-XXX" required />
                    </div>
                  </div>

                  {/* 2. Contact & Location */}
                  <h4
                    ref={el => sectionRefs.current[2] = el}
                    data-section={2}
                    className="form-section-title h6 mb-3 fw-bold border-bottom pb-2"
                    style={{ fontSize: '0.85rem' }}
                  >
                    <span className="section-number">{FORM_SECTIONS[1].number}</span>
                    <span className="text-uppercase">{FORM_SECTIONS[1].title}</span>
                  </h4>
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Email Address</label>
                      <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} placeholder="employee@company.com" />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Phone Number</label>
                      <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 555-5555" />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Street Address <span style={{ color: 'var(--ledger-seal)' }}>*</span></label>
                      <AddressAutocomplete className="form-control" value={formData.address} onChange={handleInputChange} onPlaceSelected={handlePlaceSelected} required={true} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">City</label>
                      <input type="text" className="form-control" name="city" value={formData.city} onChange={handleInputChange} />
                    </div>
                    <div className="col-sm-3 col-6">
                      <label className="form-label small fw-semibold">Province</label>
                      <select className="form-select" name="province" value={formData.province} onChange={handleInputChange}>
                        <option value="ON">ON</option>
                        <option value="BC">BC</option>
                        <option value="AB">AB</option>
                      </select>
                    </div>
                    <div className="col-sm-3 col-6">
                      <label className="form-label small fw-semibold">Postal</label>
                      <input type="text" className="form-control" name="postal_code" value={formData.postal_code} onChange={handleInputChange} />
                    </div>
                  </div>

                  {/* 3. Employment & Compensation */}
                  <h4
                    ref={el => sectionRefs.current[3] = el}
                    data-section={3}
                    className="form-section-title h6 mb-3 fw-bold border-bottom pb-2"
                    style={{ fontSize: '0.85rem' }}
                  >
                    <span className="section-number">{FORM_SECTIONS[2].number}</span>
                    <span className="text-uppercase">{FORM_SECTIONS[2].title}</span>
                  </h4>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Status</label>
                      <select className="form-select" name="employment_status" value={formData.employment_status} onChange={handleInputChange}>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="On Leave">On Leave</option>
                      </select>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Start Date</label>
                      <input type="date" className="form-control text-muted" name="employment_start_date" value={formData.employment_start_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Job Title</label>
                      <input type="text" className="form-control" name="job_title" value={formData.job_title} onChange={handleInputChange} placeholder="e.g., Software Developer" />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Pay Group</label>
                      <select className="form-select" name="pay_group" value={formData.pay_group} onChange={handleInputChange}>
                        <option value="Hourly">Hourly</option>
                        <option value="Salary">Salary</option>
                      </select>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Pay Frequency</label>
                      <select className="form-select" name="pay_frequency" value={formData.pay_frequency} onChange={handleInputChange}>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Biweekly</option>
                        <option value="semi-monthly">Semi-monthly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="col-sm-6" style={{ display: formData.pay_group === 'Salary' ? 'none' : 'block' }}>
                      <label className="form-label small fw-semibold">Hourly Rate ($)</label>
                      <input type="number" className="form-control ledger-mono" name="hourly_rate" value={formData.hourly_rate} onChange={handleInputChange} step="0.01" placeholder="0.00" />
                    </div>
                    <div className="col-sm-6" style={{ display: formData.pay_group === 'Hourly' ? 'none' : 'block' }}>
                      <label className="form-label small fw-semibold">Annual Salary ($)</label>
                      <input type="number" className="form-control ledger-mono" name="salary" value={formData.salary} onChange={handleInputChange} step="0.01" placeholder="0.00" />
                    </div>
                  </div>

                  {/* 4. Tax & Payroll Settings */}
                  <h4
                    ref={el => sectionRefs.current[4] = el}
                    data-section={4}
                    className="form-section-title h6 mb-3 fw-bold border-bottom pb-2"
                    style={{ fontSize: '0.85rem' }}
                  >
                    <span className="section-number">{FORM_SECTIONS[3].number}</span>
                    <span className="text-uppercase">{FORM_SECTIONS[3].title}</span>
                  </h4>
                  <div className="ledger-card mb-2" style={{ background: '#FBFAF7' }}>
                    <div className="card-body p-3 p-md-4">

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="vacation_pay_additional" id="vacation_pay_additional" checked={formData.vacation_pay_additional === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="vacation_pay_additional">Pay out 4% Vacation Pay on every cheque</label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="is_t4a" id="is_t4a" checked={formData.is_t4a === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="is_t4a">T4A Contractor (No statutory deductions)</label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="ei_exempt" id="ei_exempt" checked={formData.ei_exempt === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="ei_exempt">EI Exempt (e.g., controlling shareholder)</label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="cpp_exempt" id="cpp_exempt" checked={formData.cpp_exempt === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="cpp_exempt">CPP Exempt (e.g., under 18 or over 70)</label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="exclude_from_t4" id="exclude_from_t4" checked={formData.exclude_from_t4 === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="exclude_from_t4">Exclude from Year-End T4 Generation</label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <input type="checkbox" className="form-check-input" name="email_slip" id="email_slip" checked={formData.email_slip === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-medium" htmlFor="email_slip">Automatically Email Pay Slips</label>
                      </div>

                      <div className="form-check form-switch mb-3 pt-3 border-top">
                        <input type="checkbox" className="form-check-input" name="direct_deposit_active" id="direct_deposit_active" checked={formData.direct_deposit_active === 'Yes'} onChange={handleInputChange} />
                        <label className="form-check-label small fw-bold" style={{ color: 'var(--ledger-primary)' }} htmlFor="direct_deposit_active">Enable Direct Deposit</label>
                      </div>

                      {formData.direct_deposit_active === 'Yes' && (
                        <div className="row g-2 direct-deposit-box p-3 rounded-3 shadow-sm mt-2">
                          <div className="col-sm-5">
                            <label className="form-label small fw-semibold text-muted mb-1">Transit No.</label>
                            <input type="text" className="form-control form-control-sm ledger-mono" name="transit_no" value={formData.transit_no} onChange={handleInputChange} placeholder="5 digits" />
                          </div>
                          <div className="col-sm-7">
                            <label className="form-label small fw-semibold text-muted mb-1">Institution No.</label>
                            <input type="text" className="form-control form-control-sm ledger-mono" name="bank_no" value={formData.bank_no} onChange={handleInputChange} placeholder="3 digits" />
                          </div>
                          <div className="col-12 mt-2">
                            <label className="form-label small fw-semibold text-muted mb-1">Account Number</label>
                            <input type="text" className="form-control form-control-sm ledger-mono" name="account_no" value={formData.account_no} onChange={handleInputChange} placeholder="7-12 digits" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Drawer Footer (Pinned to bottom) */}
            <div className="bg-white p-3 border-top d-flex justify-content-end gap-2 mt-auto shadow-sm flex-shrink-0">
              <button type="button" className="btn btn-ledger-outline fw-semibold flex-grow-1 flex-md-grow-0" onClick={resetForm}>Cancel</button>
              <button type="submit" form="employee-form" className="btn btn-ledger-primary fw-semibold px-4 shadow-sm flex-grow-1 flex-md-grow-0">
                {editingId ? 'Save Changes' : 'Register Employee'}
              </button>
            </div>

          </div>
        </>
      )}
    </div>
  );
}

export default EmployeeManagement;